import React from 'react'

const Policy = () => {
  return (
    <div className="container mx-auto py-8 px-4 lg:px-8">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">ChatGPT Enhancer Privacy Policy</h1>
      <p className="mb-4 text-gray-700">At ChatGPT Enhancer, we prioritize the privacy and security of our users. This Privacy Policy outlines how we collect, use, and protect your personal information when you use our browser extension and services.</p>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Information We Collect:</h2>

      <ul className="list-disc list-inside mb-6 text-gray-700">
        <li>
          <b className="font-medium">Usage Data:</b> We may collect information about how you interact with the extension, your browsing behavior related to our extension, and any customization settings you apply.
        </li>
        <li>
          <b className="font-medium">Permissions:</b> ChatGPT Enhancer requires permissions to read and modify data on certain websites you visit in order to provide its features.
        </li>
        <li>
          <b className="font-medium">Conversations and Folder Feature:</b>
          <ul className="list-disc list-inside ml-6 mt-2">
            <li>
              <b className="font-medium">Conversations:</b> All your conversations remain completely private and are stored locally on your device. We do not transfer or share any of your conversations with external servers—everything stays within your browser.
            </li>
            <li>
              <b className="font-medium">Folder Feature:</b> For the folder feature, we only store the folder name and chat IDs (not the actual chats) on our servers. These IDs are simply references, like the strings you see in the URL (e.g., 66ec539b-ab14-8003-9c8c-dh54e5121ffm). This data is encrypted for extra security. Even if unencrypted, this data holds no value because only you have access to your ChatGPT authentication token, and the chat IDs alone hold no value.
            </li>
          </ul>
        </li>
        <li>
          <b className="font-medium">Prompts:</b>
          <ul className="list-disc list-inside ml-6 mt-2">
            <li>
              <b className="font-medium">Prompt Storage:</b> For prompts, we store an encrypted version of both the prompt name and its content to ensure your information remains protected. This allows you to access your prompts across different devices.
            </li>
          </ul>
          <p className="mt-2">We store these details to enable seamless access across different devices. While conversations can always be retrieved from OpenAI’s servers, storing folder and prompt information ensures you can access them from anywhere. Your personal data remains private and secure—no matter where you access it.</p>
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">How We Use Your Information:</h2>

      <ul className="list-disc list-inside mb-6 text-gray-700">
        <li>
          <b className="font-medium">App Functionality:</b> We use your information to provide you with the features and functionality of ChatGPT Enhancer, such as enabling prompt and folder access across devices.
        </li>
        <li>
          <b className="font-medium">Improvements:</b> We may use your information to improve our extension, services, and user experience.
        </li>
        <li>
          <b className="font-medium">Communication:</b> We may use your contact information to send you important information related to ChatGPT Enhancer, if you opt-in to receive such communications.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Data Sharing and Disclosure:</h2>

      <ul className="list-disc list-inside mb-6 text-gray-700">
        <li>
          <b className="font-medium">Third-Party Services:</b> We never share your private information with third-party services.
        </li>
        <li>
          <b className="font-medium">Legal Compliance:</b> We may disclose your information if required by law or in response to a valid legal request.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Data Security:</h2>

      <p className="mb-4 text-gray-700">We take the security of your information seriously and have implemented measures to protect it against unauthorized access, alteration, or destruction. All stored data (folder names, chat IDs, and prompts) is encrypted to ensure privacy.</p>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Your Choices:</h2>

      <ul className="list-disc list-inside mb-6 text-gray-700">
        <li>
          <b className="font-medium">Access and Correction:</b> You can access and update your information by contacting us directly.
        </li>
        <li>
          <b className="font-medium">Opt-Out:</b> You can opt-out of certain communications from us by uninstalling the extension.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Access, Transfer, and Deletion of Your Data:</h2>

      <p className="mb-4 text-gray-700">At ChatGPT Enhancer, we respect your rights regarding your personal data. You have the right to:</p>

      <ul className="list-disc list-inside mb-6 text-gray-700">
        <li>
          <b className="font-medium">Access Your Data:</b> You can request access to the personal data we hold about you. To do this, please contact us at <a href="mailto:aydometehan@gmail.com" className="text-blue-500 hover:underline">aydometehan@gmail.com</a> with the email address associated with your account.
        </li>
        <li>
          <b className="font-medium">Transfer Your Data:</b> If you need to transfer your personal data to another service, please contact us at <a href="mailto:aydometehan@gmail.com" className="text-blue-500 hover:underline">aydometehan@gmail.com</a>. We will provide you with a copy of your data in a structured, commonly used, and machine-readable format.
        </li>
        <li>
          <b className="font-medium">Request Deletion of Your Data:</b> You can request the deletion of your personal data by contacting us at <a href="mailto:aydometehan@gmail.com" className="text-blue-500 hover:underline">aydometehan@gmail.com</a>. Please include the email address associated with your account. To protect your privacy and ensure the security of your information, we may need to verify your identity before processing your request. Once your identity is verified, we will process your data deletion request within 30 days and notify you once the deletion is complete. Please note that we may retain certain information if required by law or for legitimate business purposes, such as maintaining financial records or resolving disputes.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Retention Periods:</h2>

      <ul className="list-disc list-inside mb-6 text-gray-700">
        <li><b className="font-medium">User Identity:</b> Retained for one year from the moment the user uninstalls the extension.</li>
        <li><b className="font-medium">Folder and Prompt Data:</b> Retained for one year from the moment the user uninstalls the extension.</li>
      </ul>

      <p className="mb-4 text-gray-700">After uninstalling the extension, users must wait for the uninstallation page to fully load in order for the one-year data retention period to begin. If the page is not fully loaded, the countdown for data deletion will not start. During this one-year period, if the user reinstalls and uses the extension, the data deletion process will be reset, and the data will be retained until the extension is uninstalled again.</p>

      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Changes to This Privacy Policy:</h2>

      <p className="mb-4 text-gray-700">We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.</p>

      <p className="mb-8 text-gray-700">If you have any questions or concerns about our Privacy Policy or practices, please contact us at <a href="mailto:aydometehan@gmail.com" className="text-blue-500 hover:underline">aydometehan@gmail.com</a>.</p>
      <p className="text-gray-700"><b>Effective Date:</b> February 20, 2025.</p>
    </div>
  )
}

export default Policy